import { accountHasEntries } from "../../lib/coverage";
import { useState } from "react";
import { Archive, CreditCard, Pencil, Plus, Trash2, Wallet } from "lucide-react";
import { api } from "../../lib/api";
import type { Account, DashboardData, Owner } from "../../types";
import type { PriceObservation } from "../../types/receipt";
import { isCard, money, shortName } from "../../lib/finance";
import { Private } from "../../components/Privacy";
import ConfirmDialog, { type ConfirmState } from "../../components/ConfirmDialog";
import SecondaryHeading from "../../app/SecondaryHeading";
import Modal from "../../components/Modal";
import { useProfileNames } from "../../components/ProfilesContext";
import AccountForm from "./AccountForm";

export interface AccountSettingsProps {
  data: DashboardData;
  prices: PriceObservation[];
  profile: Owner;
  onSaved: () => Promise<unknown>;
}

export default function AccountSettings({
  data,
  prices,
  profile,
  onSaved,
}: AccountSettingsProps) {
  const [editingAccount, setEditingAccount] = useState<Account | null | "new">(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  const profileNames = useProfileNames();
  const accounts = data.accounts || [];

  const used = (id: string) =>
    accountHasEntries(data, id) ||
    prices.some((p) => p.accountId === id);

  const types = [
    ...new Set([
      "Bank",
      "Savings",
      "Cash",
      "Credit Card",
      ...accounts.map((a) => a.type).filter(Boolean),
    ]),
  ];

  const currencies = [
    ...new Set([
      "MVR",
      "USD",
      ...accounts.map((a) => a.currency).filter(Boolean),
    ]),
  ];

  async function remove(a: Account) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const result = await api<{ archived: boolean }>("settings/accounts/remove", { id: a.id });
      if (result?.archived) {
        setNotice(
          `${shortName(a.name)} was discontinued, not removed, because it still has history such as deleted transactions or reconciliations.`,
        );
      }
      setEditingAccount(null);
      await onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel account-settings">
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
      <SecondaryHeading title="Accounts" icon={<Wallet size={18} />}>
        <button
          type="button"
          className="primary small"
          onClick={() => {
            setEditingAccount("new");
            setError("");
          }}
        >
          <Plus size={14} /> Add
        </button>
      </SecondaryHeading>

      {error && <div className="inline-error" role="alert">{error}</div>}
      {notice && <p className="settings-note" role="status">{notice}</p>}

      {editingAccount !== null && (
        <Modal
          title={editingAccount === "new" ? "New account" : "Edit account"}
          onClose={() => {
            setEditingAccount(null);
            setError("");
          }}
        >
          <AccountForm
            key={editingAccount === "new" ? "new" : editingAccount.id}
            initialAccount={editingAccount === "new" ? null : editingAccount}
            defaultOwner={profile}
            types={types}
            currencies={currencies}
            isUsed={used}
            onCancel={() => {
              setEditingAccount(null);
              setError("");
            }}
            onSaved={async () => {
              setEditingAccount(null);
              await onSaved();
            }}
          />
        </Modal>
      )}

      <div className="managed-account-list">
        {profileNames.map((owner) => (
          <div key={owner} className="managed-account-owner">
            <h3>{owner}</h3>
            {accounts
              .filter((a) => a.owner === owner)
              .map((a) => (
                <article
                  key={a.id}
                  className={`managed-account ${a.active === false ? "archived" : ""}`}
                >
                  <span className="managed-account-icon">
                    {isCard(a) ? <CreditCard size={19} /> : <Wallet size={19} />}
                  </span>
                  <span>
                    <strong>{shortName(a.name)}</strong>
                    <small>
                      {a.type} · {a.currency}
                      {a.active === false ? " · Discontinued" : ""}
                    </small>
                    <small>
                      Opening <Private>{money(a.openingBalance ?? a.balance, a.currency)}</Private> · Current <Private>{money(a.balance, a.currency)}</Private>
                    </small>
                  </span>
                  <div>
                    <button
                      type="button"
                      aria-label={`Edit ${shortName(a.name)}`}
                      onClick={() => {
                        setEditingAccount(a);
                        setError("");
                      }}
                    >
                      <Pencil size={17} />
                    </button>
                    <button
                      type="button"
                      aria-label={`${used(a.id) ? "Discontinue" : "Delete"} ${shortName(a.name)}`}
                      disabled={busy || a.active === false}
                      onClick={() =>
                        setConfirm({
                          title: used(a.id)
                            ? "Discontinue this account?"
                            : "Delete this unused account?",
                          message: used(a.id)
                            ? "It will disappear from new expenses and transfers. Existing transactions and balances remain available."
                            : "This account has no transactions. It will be removed, or discontinued instead if it still has history such as deleted transactions or reconciliations.",
                          confirmLabel: used(a.id)
                            ? "Discontinue account"
                            : "Delete account",
                          onConfirm: () => remove(a),
                        })
                      }
                    >
                      {used(a.id) ? <Archive size={17} /> : <Trash2 size={17} />}
                    </button>
                  </div>
                </article>
              ))}
          </div>
        ))}
      </div>
    </section>
  );
}
